// src/components/idcard/PhotoControls.tsx — cardholder photo: upload + live capture.
//
// Two ways in: upload from the device, or take a live photo with the camera
// (getUserMedia, front/back switch, portrait framing guide, un-mirrors selfies).
// The chosen image is downscaled + JPEG-recompressed client-side before it's
// handed to onSet as a data URL — the page POSTs that to /api/idcard/photo.
//
// Ported from the prototype's idcard-photo.jsx. The localStorage persistence
// hook is dropped: in the hub the photo lives in blob storage (see the page).

import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import { C, F } from "../../theme";
import { Button, Icons } from "../ui";

// Downscale + recompress so the upload payload stays small and the card crisp.
// Portrait-biased: ID photos are taller than wide.
export function processImage(srcDataUrl: string, maxW = 560, maxH = 720): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let w = img.width;
      let h = img.height;
      const r = Math.min(maxW / w, maxH / h, 1);
      w = Math.round(w * r);
      h = Math.round(h * r);
      const cv = document.createElement("canvas");
      cv.width = w;
      cv.height = h;
      const ctx = cv.getContext("2d");
      if (!ctx) {
        resolve(srcDataUrl);
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      resolve(cv.toDataURL("image/jpeg", 0.86));
    };
    img.onerror = () => resolve(srcDataUrl);
    img.src = srcDataUrl;
  });
}

// ─── Live camera modal ───
function CameraModal({ onClose, onCapture }: { onClose: () => void; onCapture: (dataUrl: string) => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [facing, setFacing] = useState<"user" | "environment">("user");

  const start = useCallback(async (mode: "user" | "environment") => {
    setErr(null);
    setReady(false);
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setReady(true);
    } catch (e) {
      setErr(
        e instanceof Error && e.name === "NotAllowedError"
          ? "Camera access was blocked. Allow camera permission, or use Upload instead."
          : "No camera available on this device. Use Upload instead.",
      );
    }
  }, []);

  useEffect(() => {
    void start(facing);
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, [facing, start]);

  const snap = async () => {
    const v = videoRef.current;
    if (!v) return;
    const cv = document.createElement("canvas");
    cv.width = v.videoWidth;
    cv.height = v.videoHeight;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    if (facing === "user") {
      ctx.translate(cv.width, 0);
      ctx.scale(-1, 1); // un-mirror selfie
    }
    ctx.drawImage(v, 0, 0);
    const processed = await processImage(cv.toDataURL("image/jpeg", 0.95));
    onCapture(processed);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(20,14,18,0.72)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(460px,100%)",
          background: C.card,
          borderRadius: 22,
          border: `1px solid ${C.hairline}`,
          boxShadow: C.shadowL,
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "16px 18px", borderBottom: `1px solid ${C.hairlineLt}`, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 12.5, color: C.brassDk }}>ID photo</div>
            <div style={{ fontFamily: F.serif, fontSize: 19, fontWeight: 700, color: C.ink }}>Take a photo</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              border: `1px solid ${C.hairline}`,
              background: C.card,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icons.Close s={18} c={C.inkSoft} />
          </button>
        </div>

        <div style={{ position: "relative", background: "#16121A", aspectRatio: "3/4", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {err ? (
            <div style={{ padding: 28, textAlign: "center", color: "#E9DCE4", fontFamily: F.body, fontWeight: 600, fontSize: 14, lineHeight: 1.5 }}>{err}</div>
          ) : (
            <>
              <video
                ref={videoRef}
                playsInline
                muted
                style={{ width: "100%", height: "100%", objectFit: "cover", transform: facing === "user" ? "scaleX(-1)" : "none" }}
              />
              {/* portrait framing guide */}
              <div style={{ position: "absolute", inset: "8% 18%", border: "2px dashed rgba(255,255,255,0.45)", borderRadius: 14, pointerEvents: "none" }} />
            </>
          )}
        </div>

        <div style={{ padding: 16, display: "flex", gap: 10 }}>
          {!err && (
            <Button
              variant="secondary"
              onClick={() => setFacing((f) => (f === "user" ? "environment" : "user"))}
              title="Switch camera"
              style={{ flex: "0 0 auto", minWidth: 52, padding: 12 }}
            >
              <Icons.Repeat s={18} c={C.brassDk} />
            </Button>
          )}
          <Button variant="primary" onClick={snap} disabled={!ready || !!err} style={{ flex: 1 }}>
            <Icons.User s={18} c={C.brassGlow} /> Capture
          </Button>
        </div>
      </div>
    </div>
  );
}

export interface PhotoControlsProps {
  photo: string | null;
  onSet: (dataUrl: string) => void;
  onClear: () => void;
  busy?: boolean;
}

export function PhotoControls({ photo, onSet, onClear, busy }: PhotoControlsProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [cam, setCam] = useState(false);

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async () => {
      if (typeof reader.result === "string") onSet(await processImage(reader.result));
    };
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{ display: "none" }} />
      <div style={{ display: "flex", gap: 10 }}>
        <Button variant="primary" onClick={() => setCam(true)} disabled={busy} style={{ flex: 1, whiteSpace: "nowrap" }}>
          <Icons.User s={18} c={C.brassGlow} /> Take photo
        </Button>
        <Button variant="secondary" onClick={() => fileRef.current?.click()} disabled={busy} style={{ flex: 1, whiteSpace: "nowrap" }}>
          <Icons.Plus s={18} c={C.brassDk} /> {photo ? "Replace" : "Upload"}
        </Button>
      </div>
      {photo && (
        <Button variant="ghost" onClick={onClear} disabled={busy} style={{ alignSelf: "flex-start", padding: "8px 4px", color: C.signalDk }}>
          <Icons.Close s={16} c={C.signalDk} /> Remove photo
        </Button>
      )}
      {cam && (
        <CameraModal
          onClose={() => setCam(false)}
          onCapture={(d) => {
            onSet(d);
            setCam(false);
          }}
        />
      )}
    </div>
  );
}
